/**
 * Language selector component using shadcn components
 */

import { useTranslation } from 'react-i18next';
import { Languages, Check, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { SUPPORTED_LANGUAGES, getLanguageDirection, changeLanguage, persistKnownLanguage } from '../../i18n';
import { LANGUAGE_EXPLICIT_KEY } from '../../i18n/useGeoLanguageDefault';
import { cn } from '../../lib/utils';

interface LanguageSelectorProps {
  onLanguageChange?: (code: string) => void;
  /**
   * 'icon' (default): bare globe icon button. 'labeled': outline pill showing the
   * current language name — for auth/onboarding panels where it must be discoverable.
   */
  variant?: 'icon' | 'labeled';
}

export function LanguageSelector({
  onLanguageChange,
  variant = 'icon',
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation('common');

  const handleLanguageSelect = async (code: string) => {
    await changeLanguage(code);

    // Update document direction for RTL support
    const direction = getLanguageDirection(code);
    document.documentElement.dir = direction;
    document.documentElement.lang = code;

    // Persist to the cross-subdomain cookie + localStorage so the choice follows
    // the user across `*.panluma.ai` subdomains. Mark it explicit so GeoIP
    // auto-detection (useGeoLanguageDefault) stops overriding it even if the
    // cookie is later cleared but localStorage survives — matching the sibling
    // selector in components/ui/language-selector.tsx.
    persistKnownLanguage(code);
    localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');

    // Notify parent if callback provided
    onLanguageChange?.(code);
  };

  const currentLanguage = SUPPORTED_LANGUAGES.find(
    l => l.code === i18n.language
  ) || SUPPORTED_LANGUAGES[0];

  const menuItems = (
    <DropdownMenuContent align="end" className="min-w-[180px]">
      {SUPPORTED_LANGUAGES.map((lang) => (
        <DropdownMenuItem
          key={lang.code}
          onClick={() => handleLanguageSelect(lang.code)}
          className={cn(
            'flex items-center justify-between',
            lang.code === currentLanguage.code && 'bg-accent'
          )}
        >
          <div className="flex flex-col">
            <span>{lang.nativeName}</span>
            {lang.name !== lang.nativeName && (
              <span className="text-xs text-muted-foreground">{lang.name}</span>
            )}
          </div>
          {lang.code === currentLanguage.code && (
            <Check className="h-4 w-4 text-primary" />
          )}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );

  if (variant === 'labeled') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            aria-label={t('userMenu.language')}
          >
            <Languages className="h-4 w-4" />
            <span>{currentLanguage.nativeName}</span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        {menuItems}
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('userMenu.language')}
              >
                <Languages className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('userMenu.language')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {menuItems}
    </DropdownMenu>
  );
}
